import {
  getFieldTitle,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import {
  formatFieldDisplayValueForReadOnly,
  hasFieldDisplayContent,
} from "containers/admin/clients/client/components/form-builder/form-builder-viewer-utils";
import { isObjectSchema } from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import { getStrategyData } from "containers/admin/clients/client/programs-page/program-strategy-form.utils";

export const CAMPAIGN_DETAILS_FIELD_NAMES = ["campaign", "audienceSegments", "campaignObjective"] as const;

export const CAMPAIGN_LOGIC_FIELD_NAMES = ["precedingCampaign", "followingCampaign", "otherLogic"] as const;

export const CREATIVE_ITEM_FIELD_NAMES = ["channel", "quantity", "overview", "additionalDetails"] as const;

export type SummaryField = {
  label: string;
  value: string;
};

export type CreativePopulationType = "student" | "parent";

export type CreativeRow = {
  id: string;
  populationType: CreativePopulationType;
  channel: string;
  channelValue: string;
  quantity: string;
  overview: string;
  additionalDetails: string;
};

const getObjectSchemaAtPath = (
  schema: JsonFormObjectSchema,
  sectionPath: string[],
): JsonFormObjectSchema | undefined => {
  let current: JsonFormSchema | undefined = schema;

  for (const key of sectionPath) {
    current = current?.properties?.[key];
  }

  return current && isObjectSchema(current) ? current : undefined;
};

const getDataAtPath = (data: unknown, sectionPath: string[]): Record<string, unknown> =>
  sectionPath.reduce<Record<string, unknown>>(
    (current, key) => getStrategyData(current[key]),
    getStrategyData(data),
  );

/**
 * Reads one named object section from the normalized schema + data record. Older schema
 * versions may not declare every field in `fieldNames` yet — those are skipped rather than
 * rendered as an empty row.
 */
export const buildSectionSummaryFields = (
  schema: JsonFormObjectSchema,
  data: unknown,
  sectionPath: string[],
  fieldNames: readonly string[],
): SummaryField[] => {
  const sectionSchema = getObjectSchemaAtPath(schema, sectionPath);

  if (!sectionSchema) {
    return [];
  }

  const sectionData = getDataAtPath(data, sectionPath);

  return fieldNames.flatMap((fieldName) => {
    const field = sectionSchema.properties[fieldName];

    if (!field || !hasFieldDisplayContent(field, sectionData[fieldName])) {
      return [];
    }

    return [
      {
        label: getFieldTitle(fieldName, field),
        value: formatFieldDisplayValueForReadOnly(field, sectionData[fieldName]),
      },
    ];
  });
};

const getCreativeItemSchema = (
  schema: JsonFormObjectSchema,
  populationType: CreativePopulationType,
): JsonFormObjectSchema | undefined => {
  const itemsField = schema.properties.creatives?.properties?.[populationType]?.properties?.items;
  const itemSchema = itemsField?.items;

  return itemSchema && isObjectSchema(itemSchema) ? itemSchema : undefined;
};

const getCreativeItems = (data: unknown, populationType: CreativePopulationType): unknown[] => {
  const creativesData = getStrategyData(getStrategyData(data).creatives);
  const populationData = getStrategyData(creativesData[populationType]);
  const items = populationData.items;

  return Array.isArray(items) ? items : [];
};

const buildCreativeRowsForPopulation = (
  schema: JsonFormObjectSchema,
  data: unknown,
  populationType: CreativePopulationType,
): CreativeRow[] => {
  const itemSchema = getCreativeItemSchema(schema, populationType);

  if (!itemSchema) {
    return [];
  }

  return getCreativeItems(data, populationType).map((item, index) => {
    const itemData = getStrategyData(item);

    const values = CREATIVE_ITEM_FIELD_NAMES.reduce<Record<string, string>>((acc, fieldName) => {
      const field = itemSchema.properties[fieldName];
      acc[fieldName] = field ? formatFieldDisplayValueForReadOnly(field, itemData[fieldName]) : "";

      return acc;
    }, {});

    return {
      id: `${populationType}-${index}`,
      populationType,
      channel: values.channel,
      channelValue: typeof itemData.channel === "string" ? itemData.channel : "",
      quantity: values.quantity,
      overview: values.overview,
      additionalDetails: values.additionalDetails,
    };
  });
};

export const buildCreativeRows = (schema: JsonFormObjectSchema, data: unknown): CreativeRow[] => [
  ...buildCreativeRowsForPopulation(schema, data, "student"),
  ...buildCreativeRowsForPopulation(schema, data, "parent"),
];
